<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('bus.subtitle') }}</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--primary" @click="drawer = true">
          <v-icon small>mdi-plus</v-icon>
          {{ $t('bus.newBus') }}
        </button>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          Autocars
          <span class="ov-count">{{ busList.length }}</span>
        </div>

        <div class="ov-filters">
          <!-- Sélecteur compagnie (admin uniquement) -->
          <select
            v-if="$store.state.userAuthentified.roleCode == 'RoleAdminSocieteMere'"
            v-model="selectedCompagnieForFilter"
            class="ov-select"
            @change="fetchBusListByCompagnie"
          >
            <option value="" disabled>{{ $t('bus.chooseCompany') }}</option>
            <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">
              {{ c.raisonSociale }}
            </option>
          </select>

          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('bus.searchBus')" />
          </div>

          <div class="ov-toggle">
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'table' }"
              title="Tableau"
              @click="viewMode = 'table'"
            >
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </button>
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'grid' }"
              title="Cartes"
              @click="viewMode = 'grid'"
            >
              <v-icon small>mdi-view-grid-outline</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- VUE TABLEAU -->
      <v-data-table
        v-if="viewMode === 'table'"
        :headers="getTableHeaders()"
        :items="busList"
        :search="search"
        :loading="loading"
        loader-height="2"
        class="ov-table"
        :no-data-text="$t('bus.noBusRegistered')"
        :loading-text="$t('bus.loading')"
      >
        <template v-slot:[`item.numero`]="{ item }">
          <span class="bc-numero">{{ item.numero || '—' }}</span>
        </template>
        <template v-slot:[`item.nombrePlace`]="{ item }">
          <span class="ov-badge ov-badge--success ov-badge--plain">{{ item.nombrePlace }} {{ $t('bus.places') }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="ov-row-actions bc-actions">
            <button type="button" class="ov-icon-btn" :title="$t('bus.editBus')" @click="openEditBus(item)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
            <button type="button" class="ov-icon-btn ov-icon-btn--danger" :title="$t('bus.delete')" @click="confirmDeleteBus(item)">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>

      <!-- VUE GRILLE -->
      <div v-else class="ov-card__body">
        <div v-if="filteredBusList.length === 0" class="ov-empty">
          {{ $t('bus.noBusRegistered') }}
        </div>
        <div v-else class="bc-grid">
          <div v-for="(bus, i) in filteredBusList" :key="i" class="bc-bus">
            <div class="bc-bus__head">
              <span class="bc-numero">{{ bus.numero || $t('bus.busWithoutNumber') }}</span>
              <span class="ov-badge ov-badge--success ov-badge--plain">{{ bus.nombrePlace }} {{ $t('bus.places') }}</span>
            </div>
            <div class="bc-bus__row">
              <span class="bc-bus__label">{{ $t('bus.number') }}</span>
              <span class="bc-bus__value">{{ bus.numero || '—' }}</span>
            </div>
            <div v-if="isAdminSocieteMere" class="bc-bus__row">
              <span class="bc-bus__label">{{ $t('bus.company') }}</span>
              <span class="bc-bus__value">{{ bus.raisonSociale || '—' }}</span>
            </div>
            <div class="bc-bus__foot">
              <button type="button" class="ov-icon-btn" :title="$t('bus.editBus')" @click="openEditBus(bus)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </button>
              <button type="button" class="ov-icon-btn ov-icon-btn--danger" :title="$t('bus.delete')" @click="confirmDeleteBus(bus)">
                <v-icon small>mdi-trash-can-outline</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== PANNEAU DE CRÉATION / MODIFICATION ===== -->
    <v-navigation-drawer v-model="drawer" right fixed temporary width="420" class="ov-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ editingBus ? $t('bus.editBus') : $t('bus.newBus') }}</div>
          <div class="ov-card__title">{{ editingBus ? editingBus.numero : $t('bus.createNewBus') }}</div>
        </div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="drawer = false">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="ov-panel__body">
        <div class="ov-note ov-note--info">
          <v-icon small>mdi-information-outline</v-icon>
          <span>{{ $t('bus.requiredFields') }}</span>
        </div>

        <div v-if="$store.state.userAuthentified.roleCode == 'RoleAdminSocieteMere'" class="ov-field">
          <label class="ov-label">{{ $t('bus.transportCompany') }}<span class="ov-required">*</span></label>
          <select v-model="compagnieTransportRaisonSociale" class="ov-select">
            <option :value="null" disabled>{{ $t('bus.chooseCompany') }}</option>
            <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">
              {{ c.raisonSociale }}
            </option>
          </select>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('bus.busNumber') }}<span class="ov-required">*</span></label>
          <input
            v-model.trim="newBus.numero"
            type="text"
            class="ov-input"
            :class="{ 'is-invalid': submitted && !newBus.numero }"
            :placeholder="$t('bus.busNumberPlaceholder')"
          />
          <div v-if="submitted && !newBus.numero" class="ov-error">
            {{ $t('bus.busNumberRequired') }}
          </div>
        </div>

        <div class="ov-row">
          <div class="ov-field">
            <label class="ov-label">{{ $t('bus.seatNumberStart') }}<span class="ov-required">*</span></label>
            <input
              v-model.number="newBus.numeroSiegeDebut"
              type="number"
              min="1"
              class="ov-input"
              :class="{ 'is-invalid': submitted && !newBus.numeroSiegeDebut }"
              :placeholder="$t('bus.seatNumberStartPlaceholder')"
            />
            <div v-if="submitted && !newBus.numeroSiegeDebut" class="ov-error">
              {{ $t('bus.seatNumberStartRequired') }}
            </div>
          </div>

          <div class="ov-field">
            <label class="ov-label">{{ $t('bus.seatNumberEnd') }}<span class="ov-required">*</span></label>
            <input
              v-model.number="newBus.numeroSiegeFin"
              type="number"
              min="1"
              class="ov-input"
              :class="{ 'is-invalid': submitted && (!newBus.numeroSiegeFin || newBus.numeroSiegeFin < newBus.numeroSiegeDebut) }"
              :placeholder="$t('bus.seatNumberEndPlaceholder')"
            />
            <div v-if="submitted && !newBus.numeroSiegeFin" class="ov-error">
              {{ $t('bus.seatNumberEndRequired') }}
            </div>
            <div v-else-if="submitted && newBus.numeroSiegeFin < newBus.numeroSiegeDebut" class="ov-error">
              {{ $t('bus.seatNumberEndMustBeGreater') }}
            </div>
          </div>
        </div>
      </div>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="reininitializeFormNewBus">
            <v-icon small>mdi-restore</v-icon>
            {{ $t('bus.reset') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" @click="addNewBus">
            <v-icon small>mdi-content-save-outline</v-icon>
            {{ $t('bus.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- ===== CONFIRMATION DE SUPPRESSION ===== -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <div class="ov-dialog">
        <div class="ov-dialog__head">
          <div class="ov-dialog__title">{{ $t('bus.confirmDelete') }}</div>
        </div>
        <div class="ov-dialog__body">
          <div class="ov-note ov-note--warning">
            <v-icon small>mdi-alert-outline</v-icon>
            <span>{{ $t('bus.deleteConfirmMessage', { busNumber: busToDelete && busToDelete.numero }) }}</span>
          </div>
        </div>
        <div class="ov-dialog__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="deleteDialog = false">
            {{ $t('bus.cancel') }}
          </button>
          <button type="button" class="ov-btn ov-btn--danger" @click="deleteBusFromApi">
            <v-icon small>mdi-trash-can-outline</v-icon>
            {{ $t('bus.delete') }}
          </button>
        </div>
      </div>
    </v-dialog>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_ASSOCIER_BUS_OFFRE_VOYAGE,
  API_RECUPERER_LISTE_BUS_PAR_COMPAGNIE,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../messages/messageProcess";

export default {
  name: "CreerBusCompagnieTransport",

  data() {
    return {
      errorMsg: null,
      successMsg: null,
      overlay: false,
      loading: false,
      drawer: false,
      search: "",
      viewMode: "table",
      submitted: false,

      busList: [],
      compagnieTransportList: [],
      compagnieTransportRaisonSociale: null,
      selectedCompagnieForFilter: "",
      options: { index: 0, size: 100 },
      editingBus: null,
      busToDelete: null,
      deleteDialog: false,

      busRequestObject: {
        data: { raisonSociale: null },
      },

      newBus: {
        numero: null,
        numeroSiegeDebut: null,
        numeroSiegeFin: null,
        raisonSociale: null,
      },
    };
  },

  methods: {
    async fetchBusList() {
      if (this.$store.state.userAuthentified.roleCode !== "RoleAdminSocieteMere") {
        this.busRequestObject.data.raisonSociale =
          this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        this.loading = true;
        await axios
          .post(API_RECUPERER_LISTE_BUS_PAR_COMPAGNIE, this.busRequestObject, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.data.status.code == 800) {
              this.busList = response.data.items || [];
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },

    async fetchBusListByCompagnie() {
      if (!this.selectedCompagnieForFilter) return;
      this.busRequestObject.data.raisonSociale = this.selectedCompagnieForFilter;
      this.loading = true;
      await axios
        .post(API_RECUPERER_LISTE_BUS_PAR_COMPAGNIE, this.busRequestObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.busList = response.data.items || [];
          } else {
            this.busList = [];
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.busList = [];
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async getCompagnieTransportList() {
      if (this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere") {
        await axios
          .post(API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES, this.options, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.data.status.code == 800) {
              this.compagnieTransportList = response.data.items;
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      }
    },

    reininitializeFormNewBus() {
      this.submitted = false;
      this.editingBus = null;
      this.newBus.numero = null;
      this.newBus.numeroSiegeDebut = null;
      this.newBus.numeroSiegeFin = null;
      this.compagnieTransportRaisonSociale = null;
    },

    addNewBus() {
      this.submitted = true;
      if (!this.newBus.numero  || !this.newBus.numeroSiegeDebut || !this.newBus.numeroSiegeFin) {
        this.errorMsg = this.$t("bus.fillRequiredFields");
        showErrorMessage();
        return;
      }
      if (this.newBus.numeroSiegeFin < this.newBus.numeroSiegeDebut) {
        this.errorMsg = this.$t("bus.seatNumberEndMustBeGreater");
        showErrorMessage();
        return;
      }
      if (
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere" &&
        !this.compagnieTransportRaisonSociale
      ) {
        this.errorMsg = this.$t("bus.selectTransportCompany");
        showErrorMessage();
        return;
      }
      const payload = {
        datas: [{
          id: this.editingBus ? this.editingBus.id : null,
          numero: this.newBus.numero,
          numeroSiegeDebut: this.newBus.numeroSiegeDebut,
          numeroSiegeFin: this.newBus.numeroSiegeFin,
          raisonSociale:
            this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
              ? this.compagnieTransportRaisonSociale
              : this.$store.state.userAuthentified.compagnieTransportRaisonSociale,
        }],
      };
      this.saveBus(payload);
    },

    openEditBus(bus) {
      this.newBus.numero = bus.numero;
      this.newBus.numeroSiegeDebut = bus.numeroSiegeDebut;
      this.newBus.numeroSiegeFin = bus.numeroSiegeFin;
      this.compagnieTransportRaisonSociale = bus.raisonSociale || null;
      this.editingBus = bus;
      this.submitted = false;
      this.drawer = true;
    },

    confirmDeleteBus(bus) {
      this.busToDelete = bus;
      this.deleteDialog = true;
    },

    async deleteBusFromApi() {
      if (!this.busToDelete) return;
      this.overlay = true;
      this.deleteDialog = false;
      const payload = {
        datas: [{
          numero: this.busToDelete.numero,
          raisonSociale: this.busToDelete.raisonSociale
            || this.$store.state.userAuthentified.compagnieTransportRaisonSociale,
        }],
      };
      await axios
        .post(API_ASSOCIER_BUS_OFFRE_VOYAGE, payload, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.fetchBusList();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
          this.busToDelete = null;
        });
    },

    async saveBus(payload) {
      this.overlay = true;
      await axios
        .post(API_ASSOCIER_BUS_OFFRE_VOYAGE, payload, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.drawer = false;
            this.reininitializeFormNewBus();
            this.fetchBusList();
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
        });
    },

    getTableHeaders() {
      const headers = [
        { text: this.$t('bus.number'), value: "numero" },
        { text: this.$t('bus.capacityPlaces'), value: "nombrePlace" },
      ];
      if (this.$store.state.userAuthentified.roleCode === "RoleAdminSocieteMere") {
        headers.push({ text: this.$t('bus.company'), value: "raisonSociale" });
      }
      headers.push({ text: this.$t('bus.actions'), value: "actions", sortable: false, align: "center" });
      return headers;
    },
  },

  computed: {
    isAdminSocieteMere() {
      return this.$store.state.userAuthentified.roleCode === "RoleAdminSocieteMere";
    },
    filteredBusList() {
      const term = (this.search || "").toLowerCase();
      if (!term) return this.busList;
      return this.busList.filter((b) => (b.numero || "").toLowerCase().includes(term));
    },
  },

  mounted() {
    this.getCompagnieTransportList();
    this.fetchBusList();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.bc-numero {
  font-weight: 700;
  color: $ov-navy;
}

.bc-actions {
  justify-content: center;
}

.bc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.bc-bus {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid $ov-line;
  border-radius: 14px;
}

.bc-bus__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.bc-bus__row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
}

.bc-bus__label {
  color: $ov-muted;
}

.bc-bus__value {
  font-weight: 700;
  color: $ov-ink;
}

.bc-bus__foot {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid $ov-line;
}
</style>
